import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../styles/AboutUs.css';

const AboutUs = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const fadeInLeft = {
    initial: { opacity: 0, x: -60 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6 }
  };

  const fadeInRight = {
    initial: { opacity: 0, x: 60 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const testimonials = [
    {
      name: "Vikram Mehta",
      location: "Mumbai",
      text: "The best travel experience of my life! They planned our family trip to Rajasthan perfectly, from flights to local tours and heritage hotels."
    },
    {
      name: "Ananya & Rohit Sharma",
      location: "Bangalore",
      text: "Outstanding service! Their attention to detail made our honeymoon to Kerala absolutely magical. The houseboat experience was unforgettable."
    },
    {
      name: "Deepak Gupta",
      location: "Delhi",
      text: "Five stars! They found us amazing deals for our Andaman trip and created an unforgettable itinerary with the perfect balance of relaxation and adventure."
    }
  ];

  const values = [
    {
      icon: "https://cdn-icons-png.flaticon.com/512/3004/3004592.png",
      title: "Integrity",
      description: "We operate with honesty and transparency in all our dealings."
    },
    {
      icon: "https://cdn-icons-png.flaticon.com/512/3004/3004693.png",
      title: "Excellence",
      description: "We strive for perfection in every journey we plan."
    },
    {
      icon: "https://cdn-icons-png.flaticon.com/512/3004/3004703.png",
      title: "Customer-Centric",
      description: "Your satisfaction and happiness are our top priorities."
    }
  ];

  return (
    <div className="au-about-container">
      {/* Background Image for the entire page */}
      <div className="au-background-image">
        <img src="/Assets/image/about-us-back.jpg" />
      </div>

      {/* Background Overlay */}
      <div className="au-background-overlay"></div>

      {/* Hero Section */}
      <motion.section
        className="au-hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="au-gradient-text"
        >
          About Excellent Travels
        </motion.h1>
        <motion.p
          className="au-subtitle"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          Your Trusted Partner for Extraordinary Journeys
        </motion.p>
        <motion.div
          className="au-hero-badges"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <div className="au-badge">
            <img src="https://cdn-icons-png.flaticon.com/512/3004/3004592.png" alt="Trust Badge" />
            <span>Trusted Service</span>
          </div>
          <div className="au-badge">
            <img src="https://cdn-icons-png.flaticon.com/512/3004/3004693.png" alt="Experience Badge" />
            <span>25+ Years Experience</span>
          </div>
          <div className="au-badge">
            <img src="https://cdn-icons-png.flaticon.com/512/3004/3004703.png" alt="Support Badge" />
            <span>24/7 Support</span>
          </div>
        </motion.div>
      </motion.section>

      {/* Vision & Mission Section */}
      <motion.section
        className="au-vision-mission-section"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="au-vision-mission-container">
          <motion.div
            className="au-vision-box"
            variants={fadeInLeft}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div className="au-icon-container">
              <img src="https://cdn-icons-png.flaticon.com/512/3004/3004612.png" alt="Vision Icon" />
            </div>
            <h3>Our Vision</h3>
            <p>To be the most trusted travel partner, creating unforgettable experiences that enrich lives and foster cultural understanding.</p>
          </motion.div>

          <motion.div
            className="au-mission-box"
            variants={fadeInRight}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            <div className="au-icon-container">
              <img src="https://cdn-icons-png.flaticon.com/512/3004/3004613.png" alt="Mission Icon" />
            </div>
            <h3>Our Mission</h3>
            <p>To deliver exceptional travel services with integrity and passion, ensuring every journey exceeds expectations while respecting cultures and environments.</p>
          </motion.div>
        </div>
      </motion.section>

      {/* About Section */}
      <motion.section
        className="au-about-section"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="au-section-header">
          <h2>Our Story</h2>
          <div className="au-underline"></div>
        </div>

        <div className="au-about-content">
          <div className="au-about-text">
            <p>Founded by <span className="au-highlight">Mr. M Sajid Ali</span> in 2001, Excellent Travels has grown from a small local agency to one of Bhopal's premier travel companies. With a vision to provide exceptional travel experiences, Sajid built the company on the foundations of integrity, personalized service, and deep industry knowledge.</p>

            <p>Over the years, we've helped thousands of travelers explore India's rich heritage and discover international destinations with carefully curated itineraries that blend adventure, culture, and comfort.</p>

            <p>Our team of experienced travel consultants, led by Sajid's expertise and passion, works tirelessly to ensure every journey we plan is seamless, memorable, and exceeds expectations. We pride ourselves on our attention to detail, our extensive network of partners, and our commitment to customer satisfaction.</p>

            <div className="au-founder-quote">
              <blockquote>
                "Travel is not just about visiting new places; it's about creating memories that last a lifetime. At Excellent Travels, we don't just plan trips – we craft experiences."
              </blockquote>
              <cite>- M Sajid Ali, Founder & CEO</cite>
            </div>
          </div>


        </div>
      </motion.section>

      {/* Values Section */}
      <motion.section
        className="au-values-section"
        variants={staggerContainer}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="au-section-header">
          <h2>Our Core Values</h2>
          <div className="au-underline"></div>
        </div>

        <div className="au-values-grid">
          {values.map((value, index) => (
            <motion.div
              key={index}
              className="au-value-card"
              variants={fadeInUp}
              whileHover={{
                y: -10,
                boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
              }}
            >
              <div className="au-value-icon">
                <img src={value.icon} alt={value.title} />
              </div>
              <h3>{value.title}</h3>
              <p>{value.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Benefits Section */}
      <motion.section
        className="au-benefits-section"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="au-section-header">
          <h2>Why Choose Us?</h2>
          <div className="au-underline"></div>
        </div>

        <div className="au-benefits-grid">
          <motion.div
            className="au-benefit-card"
            whileHover={{
              y: -10,
              boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
            }}
          >
            <div className="au-benefit-icon">
              <img src="https://cdn-icons-png.flaticon.com/512/2553/2553691.png" alt="Expert Planning" />
            </div>
            <h3>Expert Planning</h3>
            <p>Personalized itineraries crafted by travel experts with years of experience</p>
          </motion.div>

          <motion.div
            className="au-benefit-card"
            whileHover={{
              y: -10,
              boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
            }}
          >
            <div className="au-benefit-icon">
              <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Best Value" />
            </div>
            <h3>Best Value</h3>
            <p>Competitive prices and exclusive deals with premium service quality</p>
          </motion.div>

          <motion.div
            className="au-benefit-card"
            whileHover={{
              y: -10,
              boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
            }}
          >
            <div className="au-benefit-icon">
              <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="24/7 Support" />
            </div>
            <h3>24/7 Support</h3>
            <p>Round-the-clock assistance during your journey for peace of mind</p>
          </motion.div>

          <motion.div
            className="au-benefit-card"
            whileHover={{
              y: -10,
              boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
            }}
          >
            <div className="au-benefit-icon">
              <img src="https://cdn-icons-png.flaticon.com/512/2971/2971416.png" alt="Local Expertise" />
            </div>
            <h3>Local Expertise</h3>
            <p>Access to authentic local experiences and hidden gems</p>
          </motion.div>
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section
        className="au-testimonials-section"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="au-section-header">
          <h2>What Our Travelers Say</h2>
          <div className="au-underline"></div>
        </div>

        <div className="au-testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="au-testimonial-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.2 }}
              whileHover={{
                y: -10,
                boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
              }}
            >
              <div className="au-quote-icon">
                <img src="https://cdn-icons-png.flaticon.com/512/6364/6364450.png" alt="Quote" />
              </div>
              <p className="au-testimonial-text">"{testimonial.text}"</p>
              <div className="au-testimonial-author">
                <div className="au-author-info">
                  <h4>{testimonial.name}</h4>
                  <p className="au-location">{testimonial.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Leadership Section */}
      <motion.section
        className="au-leadership-section"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="au-section-header">
          <h2>Our Leadership</h2>
          <div className="au-underline"></div>
        </div>

        <div className="au-leadership-container">
          <motion.div
            className="au-leadership-card"
            whileHover={{
              y: -10,
              boxShadow: "0 15px 30px rgba(0, 0, 0, 0.15)"
            }}
          >
            <div className="au-leadership-image-container">
              <img
                src="/Assets/image/Sajid-ali.jpeg"
                alt="M Sajid Ali"
                className="au-leadership-image"
              />
            </div>
            <div className="au-leadership-content">
              <h3>M Sajid Ali</h3>
              <p className="au-designation">Founder & CEO</p>
              <p className="au-leadership-bio">With over 25 years in the travel industry, Sajid brings vision, leadership, and a passion for creating extraordinary travel experiences. His extensive knowledge of global destinations and commitment to customer satisfaction has established Excellent Travels as a trusted name in the industry.</p>
              <div className="au-leadership-quote">
                <blockquote>"My goal is to make every journey not just a trip, but a collection of unforgettable moments that travelers will cherish for a lifetime."</blockquote>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Call to Action */}
      <motion.section
        className="au-cta-section"
        variants={fadeInUp}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="au-cta-overlay"></div>
        <div className="au-cta-content">
          <h2>Ready to Start Your Adventure?</h2>
          <p>Let us help you create the journey of a lifetime with personalized travel experiences.</p>
          <Link to="/contact">
            <motion.button
              className="au-cta-button"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Contact Us Today
            </motion.button>
          </Link>
        </div>
      </motion.section>

      {/* Footer Spacer - provides space before footer */}
      <div className="au-footer-spacer"></div>
    </div>
  );
};

export default AboutUs;